/**
 * TackleAiChatWidget — floating chat bubble + panel for the
 * Tackle Shop AI Receptionist.
 *
 * Drop anywhere via:  <TackleAiChatWidget />
 *
 * Features:
 *   • Public — anonymous visitors can chat without signing in
 *   • Signed-in customers get their email passed → live order
 *     tracking + personalised reply
 *   • "Talk to a human" button drops a record into the Agent 2
 *     handoff queue
 *   • Shows ⚠ when the bot signals needs_human so the visitor
 *     knows escalation is in flight
 *   • Suggested-upsell pills clickable, deep-link to /tackle/{sku}
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, X, Send, Bot, UserRound, Mic, Volume2, Loader2 } from "lucide-react";
import { useTts, useWhisperDictation } from "@/lib/useVoice";

const STORAGE_SID = "hp_tackle_ai_sid_v1";

export default function TackleAiChatWidget({ context }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState(() => {
    try { return localStorage.getItem(STORAGE_SID) || ""; }
    catch { return ""; }
  });
  const [turns, setTurns] = useState(() => {
    try {
      const raw = sessionStorage.getItem("hp_tackle_turns");
      return raw ? JSON.parse(raw) : [{
        role: "bot",
        text: "G'day! I'm your Tackle Shop assistant — I can " +
              "check stock, freight rates, your order tracking, " +
              "or recommend gear. What are you after?",
      }];
    } catch {
      return [];
    }
  });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [handoffSent, setHandoffSent] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const panelRef = useRef(null);

  const { speak, stop: stopSpeaking, speaking } = useTts();
  const { listening, transcribing, transcript,
              start: startDict, stop: stopDict,
              supported: sttSupported } = useWhisperDictation();
  useEffect(() => { if (transcript) setMsg(transcript); }, [transcript]);

  useEffect(() => {
    try { sessionStorage.setItem("hp_tackle_turns", JSON.stringify(turns)); }
    catch { /* */ }
    // Scroll to the latest turn
    requestAnimationFrame(() => {
      const el = panelRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, [turns]);

  async function send() {
    const text = msg.trim();
    if (!text || busy) return;
    setMsg("");
    setTurns((t) => [...t, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/tackle/ai/chat", {
        session_id: sid || null,
        message: text,
        customer_email: user?.email || null,
        context: context || null,
      });
      if (!sid && data.session_id) {
        setSid(data.session_id);
        try { localStorage.setItem(STORAGE_SID, data.session_id); }
        catch { /* */ }
      }
      setTurns((t) => [...t, {
        role: "bot",
        text: data.reply,
        needsHuman: !!data.needs_human,
        upsell:     data.upsell_suggestions || [],
      }]);
      if (voiceOn && data.reply) {
        speak(data.reply);
      }
    } catch (e) {
      setTurns((t) => [...t, {
        role: "bot",
        text: "Sorry, hit a snag: " +
              (formatApiError(e.response?.data?.detail) || e.message),
      }]);
    } finally {
      setBusy(false);
    }
  }

  async function handoff() {
    if (!sid) return;
    setBusy(true);
    try {
      await api.post("/tackle/ai/handoff", {
        session_id: sid,
        reason:     "Customer requested human help",
        customer_email: user?.email || null,
        customer_name:  user?.name  || null,
      });
      setHandoffSent(true);
      setTurns((t) => [...t, {
        role: "system",
        text: "✓ Our team has been notified — they'll email you " +
              "within the hour. Anything else I can help with in " +
              "the meantime?",
      }]);
    } catch (e) {
      setTurns((t) => [...t, {
        role: "bot",
        text: "Couldn't queue the handoff: " + e.message,
      }]);
    } finally { setBusy(false); }
  }

  if (!open) {
    return (
      <button data-testid="tackle-ai-open"
              onClick={() => setOpen(true)}
              className="fixed bottom-6 right-6 z-50 bg-emerald-700
                          hover:bg-emerald-800 text-white rounded-full
                          h-14 px-5 shadow-lg flex items-center gap-2
                          font-semibold">
        <MessageCircle className="h-5 w-5" />
        Ask Tackle AI
      </button>
    );
  }

  return (
    <div data-testid="tackle-ai-panel"
            className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[92vw]
                        h-[520px] max-h-[80vh] bg-white border
                        border-stone-200 rounded-2xl shadow-2xl
                        flex flex-col overflow-hidden">
      <div className="bg-emerald-700 text-white px-4 py-3 flex
                          items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          <div>
            <div className="text-sm font-bold">Tackle AI Receptionist</div>
            <div className="text-[10px] opacity-80">
              Stock · freight · tracking · advice
            </div>
          </div>
        </div>
        <button data-testid="tackle-ai-close"
                onClick={() => setOpen(false)}>
          <X className="h-5 w-5" />
        </button>
      </div>

      <div ref={panelRef}
              className="flex-1 overflow-y-auto p-3 space-y-2 bg-stone-50">
        {turns.map((t, i) => (
          <div key={i}
                  className={`flex gap-2 ${
                    t.role === "user" ? "justify-end" : "justify-start"}`}>
            {t.role === "bot" && <Bot className="h-5 w-5 text-emerald-700 shrink-0 mt-1" />}
            {t.role === "system" && <div className="w-5" />}
            <div className={`max-w-[80%] text-sm rounded-2xl px-3 py-2
                              whitespace-pre-wrap ${
                                t.role === "user"
                                  ? "bg-emerald-700 text-white"
                                  : t.role === "system"
                                  ? "bg-amber-50 border border-amber-200 text-amber-900"
                                  : "bg-white border border-stone-200"}`}>
              {t.text}
              {t.upsell?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5"
                        data-testid={`tackle-ai-upsell-${i}`}>
                  {t.upsell.slice(0, 3).map((sku) => (
                    <Link key={sku}
                            to={`/tackle/product/${sku}`}
                            className="text-[11px] bg-emerald-100
                                        text-emerald-800 border border-emerald-300
                                        rounded-full px-2 py-0.5
                                        hover:bg-emerald-200">
                      ➜ {sku}
                    </Link>
                  ))}
                </div>
              )}
              {t.needsHuman && !handoffSent && (
                <button data-testid={`tackle-ai-escalate-${i}`}
                        onClick={handoff}
                        className="mt-2 text-[11px] underline
                                        text-rose-700 font-semibold">
                  ⚠ Need a human? Tap to escalate.
                </button>
              )}
            </div>
            {t.role === "user" && <UserRound className="h-5 w-5 text-stone-500 shrink-0 mt-1" />}
          </div>
        ))}
        {busy && (
          <div className="text-xs text-stone-500 italic"
                  data-testid="tackle-ai-thinking">
            …thinking
          </div>
        )}
      </div>

      <div className="border-t border-stone-200 p-2 flex gap-1">
        {sttSupported && (
          <button onMouseDown={startDict} onMouseUp={stopDict}
                    onTouchStart={startDict} onTouchEnd={stopDict}
                    data-testid="tackle-ai-mic"
                    title={transcribing ? "Transcribing…" : "Hold to speak"}
                    disabled={transcribing}
                    className={`rounded-full w-10 h-10 flex
                                  items-center justify-center border
                                  ${listening
                                      ? "bg-rose-100 border-rose-400 text-rose-700 animate-pulse"
                                      : transcribing
                                      ? "bg-amber-50 border-amber-300 text-amber-700"
                                      : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50"}`}>
            {transcribing
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Mic className="h-4 w-4" />}
          </button>
        )}
        <button onClick={() => {
                  if (speaking) stopSpeaking();
                  else setVoiceOn((v) => !v);
                }}
                data-testid="tackle-ai-voice-toggle"
                title={voiceOn ? "Voice replies ON" : "Voice replies OFF"}
                className={`rounded-full w-10 h-10 flex items-center
                                justify-center border ${
                                  voiceOn
                                    ? "bg-emerald-100 border-emerald-500 text-emerald-700"
                                    : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50"}`}>
          <Volume2 className="h-4 w-4" />
        </button>
        <input value={msg} onChange={(e) => setMsg(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  data-testid="tackle-ai-input"
                  placeholder="Ask about a product, stock or order…"
                  disabled={busy}
                  className="flex-1 px-3 py-2 border border-stone-300
                              rounded-full text-sm focus:outline-none
                              focus:border-emerald-700" />
        <button onClick={send} disabled={busy || !msg.trim()}
                  data-testid="tackle-ai-send"
                  className="bg-emerald-700 hover:bg-emerald-800
                              disabled:opacity-40 text-white rounded-full
                              w-10 h-10 flex items-center justify-center">
          <Send className="h-4 w-4" />
        </button>
      </div>
      <button onClick={handoff} disabled={handoffSent || !sid || busy}
              data-testid="tackle-ai-talk-human"
              className="border-t border-stone-200 px-3 py-2 text-xs
                          text-stone-600 hover:bg-stone-50
                          disabled:opacity-50">
        {handoffSent ? "✓ Queued for human agent"
                              : "Talk to a human →"}
      </button>
    </div>
  );
}
